/**
 * Application Entry Point.
 *
 * Mounts the React application to the DOM and wraps it with global providers
 * (React Query, Router). StrictMode is enabled to surface potential issues
 * during development.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import { useThemeStore } from "./stores/themeStore";
import "./index.css";

// Initialize theme before first render to prevent flash.
useThemeStore.getState().initialize();

/**
 * React Query client with sensible defaults for a data-heavy application.
 * - staleTime: Data stays fresh for 30 seconds before background refetch.
 * - retry: Failed queries retry once before surfacing the error.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
